// --- NEW FILE: src/components/RFQPublicDiscussionPage.js ---

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

const RFQPublicDiscussionPage = () => {
    const { token } = useParams();
    const [discussion, setDiscussion] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const fetchDiscussion = useCallback(async () => {
        try {
            const response = await api.get(`/rfqs/public/discussion/${token}/`);
            setDiscussion(response.data);
            setError(null);
        } catch (err) {
            console.error("Error fetching discussion:", err);
            setError("Could not load the discussion. The link may be invalid or expired.");
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchDiscussion();
    }, [fetchDiscussion]);

    useEffect(() => {
        scrollToBottom();
    }, [discussion?.messages]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        setIsSubmitting(true);
        setError(null);
        try {
            const response = await api.post(`/rfqs/public/discussion/${token}/`, { message: newMessage });
            // Add the new message to the state to update UI instantly
            setDiscussion(prev => ({
                ...prev,
                messages: [...prev.messages, response.data]
            }));
            setNewMessage('');
        } catch (err) {
            console.error("Error submitting message:", err);
            setError("Failed to send message. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) return <div className="text-center p-10 text-xl">Loading Discussion...</div>;
    if (error) return <div className="text-center p-10 text-red-600 font-bold text-xl">{error}</div>;
    if (!discussion) return <div className="text-center p-10">Discussion not found.</div>;

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col items-center py-8">
            <div className="w-full max-w-3xl bg-white shadow-lg rounded-lg border border-gray-200 flex flex-col h-[85vh]">
                <header className="p-4 border-b">
                    <h1 className="text-xl font-bold text-gray-800">Project Discussion</h1>
                    <p className="text-sm text-gray-600">RFQ: {discussion.rfq_name}</p>
                </header>

                <main className="flex-1 p-4 overflow-y-auto bg-gray-50">
                    <div className="space-y-4">
                        {discussion.messages.map(msg => (
                            <div key={msg.id} className={`flex items-end gap-2 ${msg.sender_type === 'VENDOR' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-md p-3 rounded-lg ${msg.sender_type === 'VENDOR' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'}`}>
                                    <p className="font-bold text-sm">{msg.sender_name}</p>
                                    <p className="whitespace-pre-wrap">{msg.message}</p>
                                    <p className="text-xs mt-1 text-right opacity-70">{new Date(msg.created_at).toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                </main>

                <footer className="p-4 border-t bg-white">
                    {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
                    <form onSubmit={handleSubmit} className="flex gap-2">
                        <textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your message..."
                            className="flex-1 p-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                            rows="2"
                            disabled={isSubmitting}
                        />
                        <button
                            type="submit"
                            className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:opacity-50"
                            disabled={isSubmitting || !newMessage.trim()}
                        >
                            {isSubmitting ? 'Sending...' : 'Send'}
                        </button>
                    </form>
                </footer>
            </div>
        </div>
    );
};

export default RFQPublicDiscussionPage;